/**
 * MobileOutputs — a browsable list of recently SENT research outputs.
 *
 * CLUSTER-4 (research-visibility uplift). Desktop can see what research the firm has shipped — adviser
 * comms, IC briefs, Zoom docs etc. — but mobile had no way to glance at "what did we send recently?".
 * This screen lists the most-recent SENT output drafts the caller is permitted to see, each showing its
 * kind, target, and sent date, plus a clear "has attachment" affordance with a copyable reference id when
 * the payload carries an app document or Asana permalink.
 *
 * Route: /partner/m/outputs
 *
 * DATA — `mobile_outputs()` SECURITY DEFINER RPC, gated by can_read_research() (raising 42501 otherwise).
 * It returns a JSON ARRAY directly:
 *   [{ id, kind, external_id, status, target, sent_at, has_file }, …]
 * (status is always 'sent'; LIMIT 40; sent_at desc NULLS LAST). NB the SQL aliases BOTH `kind` and
 * `target` to output_kind (S1: there is no separate target column), so they're the same value; we render
 * the kind prominently and only show a distinct target line when it actually differs. has_file is derived
 * from the payload (app_document_id / asana_permalink presence) — it tells us an attachment EXISTS, but
 * there is no mobile deep-link to open it, so we surface the external reference as copyable text rather
 * than pretending to open a document we can't resolve.
 *
 * READ-ONLY and offline-aware, mirroring the sibling mobile pages exactly:
 *   • ONE useCachedQuery write-throughs the resolved array (queryFn THROWS on { error } so the cached
 *     last-known-good keeps serving offline rather than being overwritten with a partial/empty value).
 *   • A stable EMPTY array guarantees the screen is never blank on an offline cold start.
 *   • The "Saved <relative time>" header hint + amber-when-offline colour mirror the sibling pages.
 *   • A non-research caller hits the 42501 gate → a graceful no-access card when nothing is cached.
 *
 * The generated supabase types are stale for this RPC, so the call is cast through `any` (matching the
 * other cluster screens). No writes. Web/desktop are untouched (this is a /partner/m/* surface).
 */
import { useCallback, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import { ChevronLeft, Send, Paperclip, Copy, Check } from 'lucide-react';
import { useIsOnline } from '@/hooks/useIsOnline';
import { useCachedQuery } from '@/lib/offline/cachedQuery';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { humanise } from '@/components/research-hub/labels';

// ---------------------------------------------------------------------------
// Shapes — exactly the keys mobile_outputs() builds.
// ---------------------------------------------------------------------------

interface OutputRow {
  id: string;
  kind: string | null;
  external_id: string | null;
  status: string | null;
  target: string | null;
  sent_at: string | null;
  has_file: boolean | null;
}

const EMPTY_OUTPUTS: OutputRow[] = [];

// Postgres SQLSTATE for insufficient_privilege — the RPC raises this without research read access.
const NO_ACCESS_CODE = '42501';

function errorCode(err: unknown): string | undefined {
  if (err && typeof err === 'object' && 'code' in err) {
    const code = (err as { code?: unknown }).code;
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
}

async function fetchOutputs(): Promise<OutputRow[]> {
  const { data, error } = await (supabase.rpc as any)('mobile_outputs');
  // MUST throw so useCachedQuery keeps the cached placeholder offline and the 42501 state can surface.
  if (error) throw error;
  // The RPC returns a JSON array directly (COALESCE(jsonb_agg(...), '[]')).
  return Array.isArray(data) ? (data as OutputRow[]) : [];
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatRelative(iso: string | null): string | null {
  if (!iso) return null;
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function MobileOutputs() {
  const online = useIsOnline();
  const { toast } = useToast();

  const list = useCachedQuery<OutputRow[]>({
    queryKey: ['mobile-outputs'],
    cacheKey: 'mobile.outputs',
    queryFn: fetchOutputs,
  });

  const items = list.data ?? EMPTY_OUTPUTS;

  // Which row's reference was just copied (transient tick on the button).
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const copyReference = useCallback(
    async (id: string, reference: string) => {
      try {
        await navigator.clipboard.writeText(reference);
        setCopiedId(id);
        window.setTimeout(() => setCopiedId((cur) => (cur === id ? null : cur)), 1500);
        toast({ title: 'Reference copied', description: 'Paste it into your document tool to open.' });
      } catch {
        toast({
          variant: 'destructive',
          title: 'Couldn’t copy',
          description: 'Copy isn’t available on this device.',
        });
      }
    },
    [toast],
  );

  const showStale = list.isFromCache && list.cachedAt != null;
  let staleLabel: string | null = null;
  if (showStale && list.cachedAt) {
    try {
      staleLabel = `Saved ${formatDistanceToNow(new Date(list.cachedAt), { addSuffix: true })}`;
    } catch {
      staleLabel = 'Showing saved data';
    }
  }

  const noAccess = errorCode(list.error) === NO_ACCESS_CODE;
  // No cached data AND access denied → graceful no-access state (don't blank).
  const showNoAccess = noAccess && items.length === 0;
  // Hard error only when there's nothing cached AND it isn't the no-access gate (e.g. signed-out 28000).
  const hardError = !showNoAccess && list.isError && !list.isFromCache && list.data === undefined;

  return (
    <div className="min-h-screen bg-background px-4 pb-8 pt-[calc(1.25rem+env(safe-area-inset-top))]">
      <header className="mb-4">
        <Link
          to="/partner/m/home"
          className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          Home
        </Link>
        <h1 className="font-heading text-2xl font-semibold text-primary">Outputs</h1>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
          {items.length > 0 && (
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">
              {items.length} recent send{items.length === 1 ? '' : 's'}
            </p>
          )}
          {showStale && staleLabel && (
            <p
              className={cn(
                'text-[11px] font-medium uppercase tracking-wide',
                online ? 'text-muted-foreground/70' : 'text-amber-600',
              )}
            >
              {staleLabel}
            </p>
          )}
        </div>
      </header>

      <div className="space-y-3">
        {showNoAccess ? (
          <section className="rounded-xl border border-border bg-card p-4">
            <h2 className="font-heading text-sm font-semibold">No access</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              You don’t have access to research outputs. This view is available to the research team.
            </p>
          </section>
        ) : hardError ? (
          <section className="rounded-xl border border-border bg-card p-4">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">Couldn’t load</p>
            <p className="mt-2 text-sm text-muted-foreground">
              {online
                ? 'We couldn’t load recent outputs just now. Pull back in a moment.'
                : 'You’re offline and there’s no saved output history yet. This will fill in once you reconnect.'}
            </p>
          </section>
        ) : items.length === 0 ? (
          <section className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Send className="h-5 w-5" aria-hidden="true" />
              </span>
              <h2 className="font-heading text-sm font-semibold">Nothing sent yet</h2>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              Research outputs you’ve sent — adviser comms, briefs and docs — will appear here.
            </p>
            {!online && (
              <p className="mt-2 text-[11px] uppercase tracking-wide text-amber-600">
                You’re offline — this will refresh once you reconnect.
              </p>
            )}
          </section>
        ) : (
          <ul className="space-y-3">
            {items.map((o) => {
              const when = formatRelative(o.sent_at);
              const kind = o.kind ? humanise(o.kind) : 'Output';
              // kind and target are the same value (both output_kind) per the SQL; only show a distinct
              // target line when it genuinely differs (future-proofing if target is ever derived apart).
              const target =
                o.target && o.target !== o.kind ? humanise(o.target) : null;
              const reference = o.external_id ?? null;
              const copied = copiedId === o.id;
              return (
                <li key={o.id}>
                  <section
                    className="rounded-xl border border-border bg-card p-4"
                    aria-label={`Sent output: ${kind}`}
                  >
                    <div className="flex items-start gap-2">
                      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <Send className="h-4 w-4" aria-hidden="true" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <h2 className="font-heading text-sm font-semibold leading-snug">{kind}</h2>
                        {target && (
                          <p className="mt-0.5 text-[13px] text-muted-foreground">To {target}</p>
                        )}
                        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                          <span className="rounded-full bg-green-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-green-700 dark:text-green-400">
                            Sent
                          </span>
                          {o.has_file && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                              <Paperclip className="h-3 w-3" aria-hidden="true" />
                              Attachment
                            </span>
                          )}
                          {when && (
                            <span className="text-[11px] uppercase tracking-wide text-muted-foreground/70">
                              {when}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Share/open affordance: there's no in-app deep-link to the document, so we surface
                        the external reference as copyable text the user can paste into their doc tool. */}
                    {o.has_file && reference && (
                      <button
                        type="button"
                        onClick={() => copyReference(o.id, reference)}
                        aria-label="Copy the document reference"
                        className={cn(
                          'mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
                          'border-primary/30 bg-primary/10 text-primary active:bg-primary/20',
                        )}
                      >
                        {copied ? (
                          <Check className="h-4 w-4" aria-hidden="true" />
                        ) : (
                          <Copy className="h-4 w-4" aria-hidden="true" />
                        )}
                        {copied ? 'Copied reference' : 'Copy document reference'}
                      </button>
                    )}
                  </section>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
